// TODO: rename to useJobPositionPerks
import { differenceInMonths } from 'date-fns';

import { EmploymentType } from '@pflegia/types/api/candidate/employment-type';
import { CareType, IntensiveCareType, PRAXIA_CARE_TYPES, EmployerType } from '@pflegia/types/api/employer';
import { JobPosition } from '@pflegia/types/api/job-position';
import { useParseSalary } from '@pflegia/ui/shared/hooks/use-parse-salary';
import { removeCountryName } from '@pflegia/ui/shared/utils/remove-country-name';
import { isDefined } from '@pflegia/util/common/type-guards';

import { careTypeToTypeOfEmployer } from '@shared/application/care-type-to-type-of-employer';

import { MONTHS_BEFORE_SALARY_EXPIRY } from '../application/constants';

export interface PerksList {
  employer?: string;
  address?: string;
  departments?: string[];
  vacations?: string;
  salary?: string; // SalaryLow - SalaryHigh
  workingHours?: string;
  typeOfHospital?: string;
  numberOfBeds?: string;
  employmentType?: string[];
  contractTypes?: string[];
  overtimeRegulations?: string[];
  salaryNegotiations?: string[];
  moreVacancies?: string; // SKIP THIS
  startOfWork?: string;
  typeOfIntensiveCare?: string;
  distanceFromHome?: string; // This has to be implemented (DirectionsMap uses something similar)
  numberOfPatients?: string;
  distanceOfTravel?: string; // SKIP THIS - IT SEEMS REDUNDANT AND CAN BE REMOVED
  carForPrivateUse?: string;
  areaOfPatients?: string; // Relation to GeoLocation Table
  teamSize?: string;
  applicationProcess?: string;
  numberOfDoctors?: string;
  nursingShare?: string;
  postalCode?: string;
}

export interface HighlightsList {
  headquarters?: string;
  offerApartments?: boolean;
  averageTime?: number;
  averagePeriod?: string;
  adjustableDistance?: boolean;
  interviewWith?: string[];
}

const mapEmploymentTypes = (jobPosition: JobPosition) =>
  jobPosition?.employmentTypes?.map(employmentType => {
    if (employmentType === EmploymentType.PartTime && jobPosition.partTimeHours) return `${employmentType}:${jobPosition.partTimeHours}`;
    else if (employmentType === EmploymentType.FullTime && jobPosition.fullTimeHours)
      return `${employmentType}:${jobPosition.fullTimeHours}`;
    return employmentType;
  }) || [];

const getPerksByCareType = (jobPosition: JobPosition, salary: string | null, careType: CareType) => {
  const isAmbulant = careType === CareType.Ambulant || careType === CareType.IntensiveAmbulant;
  const isIntensiveCare = [CareType.IntensiveAmbulant, CareType.IntensiveStationary].includes(careType);
  let typeOfIntensiveCare;
  if (isIntensiveCare) {
    typeOfIntensiveCare = careType === CareType.IntensiveAmbulant ? IntensiveCareType.OneToOne : IntensiveCareType.Wg;
  }
  const isPraxiaCareType = PRAXIA_CARE_TYPES.includes(careType);
  const typeOfEmployer = careTypeToTypeOfEmployer(careType);
  const showPatientCount =
    !isAmbulant && isDefined(typeOfEmployer) && ![EmployerType.DentalPractice, EmployerType.DoctorOffice].includes(typeOfEmployer);

  return {
    employer: jobPosition.employer.name,
    address: jobPosition.geoLocation?.formattedAddress,
    vacations: jobPosition.vacationDays,
    salary: jobPosition.showSalary && jobPosition.employer.showSalary ? salary : null,
    numberOfBeds: isAmbulant || isPraxiaCareType ? null : jobPosition.beds || jobPosition.employer?.beds,
    employmentType: mapEmploymentTypes(jobPosition),
    contractTypes: jobPosition.contractTypes?.map((element: { title: string }) => element.title),
    overtimeRegulations: jobPosition.employer?.overtimeCompensations?.map((element: { title: string }) => element.title),
    salaryNegotiations: jobPosition.employer?.salaryNegotiations?.map((element: { title: string }) => element.title),
    startOfWork: jobPosition.canStartNow ? 'Immediately' : jobPosition.canStartDate,
    teamSize: jobPosition.teamSize || jobPosition.employer?.teamSize,
    applicationProcess: isPraxiaCareType ? jobPosition.applicationProcess || jobPosition.employer?.applicationProcess : null,
    numberOfDoctors: isPraxiaCareType ? jobPosition.numberOfDoctors || jobPosition.employer?.numberOfDoctors : null,
    nursingShare: jobPosition.careShare ? `${jobPosition.careShare}%` : null,
    typeOfIntensiveCare,
    numberOfPatients: showPatientCount && isPraxiaCareType ? jobPosition.beds || jobPosition.employer?.beds : null,
    carForPrivateUse: jobPosition.employer.carForPrivateUse,
    areaOfPatients: jobPosition.employer?.patientAreas?.map(location => removeCountryName(location.formattedAddress)),
    patientsPerTour: isAmbulant ? jobPosition.employer?.patients : null,
    departments: [CareType.Hospital, CareType.RehabClinic].includes(careType) ? jobPosition.departments?.map(({ name }) => name) : null,
    typeOfHospital: careType === CareType.Hospital || careType === CareType.RehabClinic ? jobPosition.employer?.hospitalType : null,
  };
};

const getHighlights = (jobPosition: JobPosition): HighlightsList => ({
  headquarters: removeCountryName(jobPosition.employer.geoLocation?.formattedAddress ?? jobPosition.geoLocation.formattedAddress),
  offerApartments: jobPosition.employer.offerApartments,
  averageTime: jobPosition.employer.averageTime,
  averagePeriod: jobPosition.employer.averagePeriod,
  adjustableDistance: jobPosition.employer.adjustableDistance,
  interviewWith: jobPosition.employer.interviewTypes,
});

export const useParseJobPositionForPerks = (jobPosition?: JobPosition): { perks: PerksList; highlights: HighlightsList } => {
  const isLeasing = jobPosition?.employer?.careType === CareType.Leasing;
  const parsedSalary = useParseSalary(
    {
      low: jobPosition?.salaryLow,
      high: jobPosition?.salaryHigh,
    },
    isLeasing,
    jobPosition?.fullTimeHours,
  );

  if (!jobPosition) return { perks: {}, highlights: {} };

  const salaryUpdatedAt = jobPosition.salaryLastChangedAt ?? jobPosition.createdAt;
  const diffInMonths = differenceInMonths(new Date(), salaryUpdatedAt);
  const salary = !jobPosition.isInitiative || diffInMonths <= MONTHS_BEFORE_SALARY_EXPIRY ? parsedSalary : null;
  const everyPerk = getPerksByCareType(jobPosition, salary, jobPosition.employer?.careType);
  const allPerks = Object.fromEntries(
    Object.entries(everyPerk).filter(([, value]) => value && (typeof value === 'string' || Array.isArray(value) ? value.length : true)),
  );
  const highlights = getHighlights(jobPosition);

  allPerks.postalCode = jobPosition.geoLocation?.postalCode;
  return { perks: allPerks, highlights };
};
